<script setup>
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Earth, LockKeyhole, Info, ArrowUpRight } from 'lucide-vue-next';
import { computed, watch, onMounted, ref } from 'vue';
import { i18n } from '@/i18n';
import Tooltip2 from '@/components/Tooltip2.vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const props = defineProps({
  isPro: {
    type: Boolean,
    default: false,
  },
  class: String,
  open: Boolean,
});

defineExpose({
  getValue: () => {
    return modelValue.value;
  },
});

const createType = ref('public');
const cacheValue = ref('');

const options = [
  {
    icon: Earth,
    label: i18n.t('create.projectType.public.label'),
    description: i18n.t('create.projectType.public.description'),
    tips: i18n.t('create.projectType.public.tips'),
    value: 'public',
  },
  {
    icon: LockKeyhole,
    label: i18n.t('create.projectType.private.label'),
    tag: i18n.t('create.projectType.private.tag'),
    description: i18n.t('create.projectType.private.description'),
    value: 'private',
    requiresPro: true,
  },
];

const emit = defineEmits(['update:model-value', 'update:open']);

const isOpen = computed({
  get: () => props.open,
  set: (v) => emit('update:open', v),
});

const cachekey = 'create:projectType';

// Load from localStorage if available
const getInitialValue = () => {
  const savedValue = localStorage.getItem(cachekey);
  return savedValue || 'public';
};

// Initialize createType with localStorage value
onMounted(() => {
  cacheValue.value = getInitialValue();
});

// Save to localStorage when createType changes
watch(
  () => cacheValue.value,
  (newValue) => {
    emit('update:model-value', newValue);
    localStorage.setItem(cachekey, newValue);
  }
);

const modelValue = computed({
  get: () => {
    if (!props.isPro) return 'public';
    return cacheValue.value || createType.value;
  },
  set: (v) => {
    if (props.isPro) {
      cacheValue.value = v;
      createType.value = v;
    } else {
      cacheValue.value = 'public';
    }
  },
});

const currentValue = computed(() => {
  return options.find((option) => option.value === modelValue.value);
});

const handleUpgrade = () => {
  router.push('/pricing');
};

function handleTypeMouseEnter(event, optionValue) {
  if (modelValue.value !== optionValue) {
    event.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
  }
}

function handleTypeMouseLeave(event, optionValue) {
  if (modelValue.value !== optionValue) {
    event.currentTarget.style.backgroundColor = '';
  }
}
</script>
<template>
  <Tooltip2 :text="$t('create.projectType.title')">
    <Select v-model="modelValue" v-model:open="isOpen">
      <SelectTrigger
        :class="props.class"
        class="group pl-0 px-1 gap-0 hover:bg-black/5 hover:border-black/20 data-[state=open]:bg-black/5 data-[state=open]:border-black/20"
      >
        <SelectValue>
          <div class="flex items-center gap-0 flex-1">
            <div class="shrink-0 size-6 flex items-center justify-center">
              <component :is="currentValue?.icon" class="size-3.5 text-primary" />
            </div>
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent
        class="rounded-[20px] backdrop-blur-[60px]"
        style="
          background-color: rgba(255, 255, 255, 0.85);
          border: 1px solid rgba(0, 0, 0, 0.07);
          box-shadow:
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 8px 10px -6px rgba(0, 0, 0, 0.1);
        "
      >
        <SelectGroup class="px-2">
          <SelectItem
            v-for="model in options"
            :key="model.value"
            :value="model.value"
            :indicator="false"
            class="transition-colors duration-200 rounded-lg p-3 mb-0.5"
            :class="{
              'opacity-50 pointer-events-none': model.requiresPro && !isPro,
            }"
            :style="modelValue === model.value ? 'background-color: rgba(0, 0, 0, 0.05)' : ''"
            @mouseenter="handleTypeMouseEnter($event, model.value)"
            @mouseleave="handleTypeMouseLeave($event, model.value)"
          >
            <div class="flex w-64 items-start gap-3">
              <component :is="model.icon" class="size-4 text-primary mt-0.5" />
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  <span
                    class="text-base font-normal"
                    :class="modelValue === model.value ? 'text-black' : 'text-black/50'"
                    >{{ model.label }}</span
                  >
                  <span
                    v-if="model.tag"
                    class="text-xs px-2.5 py-0.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full font-medium shadow-sm"
                  >
                    {{ model.tag }}
                  </span>
                </div>
                <p
                  class="text-xs font-normal leading-relaxed"
                  :class="modelValue === model.value ? 'text-black' : 'text-black/50'"
                >
                  {{ model.description }}
                </p>
              </div>
              <Tooltip2 v-if="model.tips" :text="model.tips" contentClass="w-64">
                <Info class="size-4 text-black/40 hover:text-black/60 transition-colors" />
              </Tooltip2>
            </div>
          </SelectItem>
        </SelectGroup>
        <div v-if="!isPro" class="px-2 pb-2">
          <div class="border-t border-black/5 pt-2">
            <button
              @click="handleUpgrade"
              class="w-full flex items-center justify-center gap-1.5 text-base font-normal text-blue-600 hover:text-blue-700 transition-colors py-2 px-3 rounded-lg bg-blue-600/5 hover:bg-blue-600/10"
            >
              <span>{{ $t('create.projectType.upgrade') }}</span>
              <ArrowUpRight class="size-4.5" />
            </button>
          </div>
        </div>
      </SelectContent>
    </Select>
  </Tooltip2>
</template>
